import { useLayoutEffect, type RefObject } from 'react';

/** Anchor a sign's authored contact point to its authored terrain path on resize.
 * Both layers retain their matching parallax speed, so scrolling needs no work. */
export function useCounterGroundAnchor(ref: RefObject<HTMLDivElement | null>, season: string) {
  useLayoutEffect(() => {
    const scene = ref.current;
    if (!scene) return;
    let frame = 0;
    let adjustment = 0;
    let settlingFrames = 0;
    let sign: HTMLElement | null = null;
    function align() {
      frame = 0;
      if (!scene) return;
      sign = scene.querySelector<HTMLElement>('.landscape__foreground-content:has(svg[data-ground-foot])');
      let terrain = scene.querySelector<SVGPathElement>('[data-counter-ground]');
      const artwork = sign?.querySelector<SVGSVGElement>('svg[data-ground-foot]');
      if (!sign || !terrain || !artwork) return;
      if (getComputedStyle(sign).getPropertyValue('--counter-ground-snap').trim() !== '1') {
        adjustment = 0;
        sign.style.removeProperty('--counter-ground-adjust');
        sign.style.removeProperty('right');
        return;
      }
      const signMatrix = artwork.getScreenCTM();
      const groundMatrix = terrain.getScreenCTM();
      if (!signMatrix || !groundMatrix) return;
      const [x, y] = artwork.dataset.groundFoot!.split(' ').map(Number);
      const foot = new DOMPoint(x, y).matrixTransform(signMatrix);
      // Two authored hill anchors, selected by the theme's phone/tablet preset.
      const anchorX = Number.parseFloat(getComputedStyle(sign).getPropertyValue('--counter-ground-anchor-x'));
      if (!Number.isFinite(anchorX)) return;
      let targetX = new DOMPoint(anchorX, 0).matrixTransform(groundMatrix).x;
      const edgeGap = Number.parseFloat(getComputedStyle(sign).getPropertyValue('--counter-ground-edge-gap'));
      if (Number.isFinite(edgeGap)) {
        const sceneBounds = scene.getBoundingClientRect();
        const signBounds = sign.getBoundingClientRect();
        targetX = Math.max(sceneBounds.left + edgeGap + foot.x - signBounds.left,
          Math.min(targetX, sceneBounds.right - edgeGap - (signBounds.right - foot.x)));
      }
      const currentRight = Number.parseFloat(getComputedStyle(sign).right) || 0;
      sign.style.right = `${currentRight - (targetX - foot.x)}px`;
      const native = new DOMPoint(targetX, groundMatrix.f).matrixTransform(groundMatrix.inverse());
      const segment = [...scene.querySelectorAll<SVGPathElement>('[data-counter-ground]')]
        .find(path => { const bounds = path.getBBox(); return native.x >= bounds.x && native.x <= bounds.x + bounds.width; });
      // Outside the authored canvas, the foreground continues at its bottom edge.
      let groundY = terrain.ownerSVGElement!.viewBox.baseVal.height;
      if (segment) {
        terrain = segment;
        const bounds = terrain.getBBox();
        groundY = bounds.y + bounds.height - .01;
        // The lower closure can slope too; enter the fill before finding its top.
        while (groundY > bounds.y && !terrain.isPointInFill(new DOMPoint(native.x, groundY))) groundY -= .5;
        if (groundY <= bounds.y) return;
        while (groundY > bounds.y && terrain.isPointInFill(new DOMPoint(native.x, groundY - 2))) groundY -= 2;
        let low = groundY - 2;
        for (let i = 0; i < 8; i++) {
          const middle = (low + groundY) / 2;
          if (terrain.isPointInFill(new DOMPoint(native.x, middle))) groundY = middle;
          else low = middle;
        }
      }
      const ground = new DOMPoint(native.x, groundY).matrixTransform(groundMatrix);
      const inset = Number.parseFloat(getComputedStyle(sign).getPropertyValue('--counter-ground-inset')) || 2;
      const delta = ground.y + inset - foot.y;
      adjustment += delta;
      sign.style.setProperty('--counter-ground-adjust', `${adjustment}px`);
      if (Math.abs(delta) > .1 && settlingFrames++ < 4) frame = requestAnimationFrame(align);
    }
    const schedule = () => {
      cancelAnimationFrame(frame);
      settlingFrames = 0;
      frame = requestAnimationFrame(align);
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(scene);
    scene.querySelectorAll('.landscape__foreground-content, .landscape__layer:has([data-counter-ground]), svg[data-ground-foot]').forEach(element => observer.observe(element));
    window.addEventListener('resize', schedule);
    // Theme styles are inserted during the same commit; measure after insertion.
    schedule();
    let mounted = true;
    void document.fonts.ready.then(() => { if (mounted) schedule(); });
    return () => {
      mounted = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      sign?.style.removeProperty('--counter-ground-adjust');
      sign?.style.removeProperty('right');
    };
  }, [ref, season]);
}
